/**
 * "Asked <person>, no answer yet" for one of the reader's own live items
 * (UX round 2026-10-06).
 *
 * An item only counted as "waiting on someone else" when its posture note
 * said so (waitingOnView). Asks sent from the item did not count: the
 * long-running A&H item, pulled back with two open asks to Cliff and Laura,
 * sat under "Waiting on your call" while "Waiting on others" read 0, in two
 * explorers' runs. An open ask is the plainest sign the next move is
 * someone else's, so it counts too: who was asked, oldest ask first, and
 * since when. An answered ask is no longer open, so the item comes back to
 * the reader's own lanes, with the answer said on its row.
 *
 * Pure logic (repo convention: logic in .ts, components in .tsx).
 */
import type { DoorThreadRead } from '../../services/qcpInvestigations';
import type { DoorDataRequestRead } from '../../services/qcpDataRequests';
import { isWaitingAsk } from '../../components/quality/askKind';
import { asksForThread } from './qualityWorkspaceView';
import { waitingOn } from './waitingOnView';

export interface AskWait {
  /** The people asked, oldest ask first, each once. */
  who: string[];
  /** When the oldest open ask went out (ISO); null when unknown. */
  since: string | null;
}

function sentAt(r: Pick<DoorDataRequestRead, 'sent_at' | 'created_at'>): string {
  return r.sent_at ?? r.created_at;
}

/** The open asks on this item, as who and since when; null when none. */
export function openAskWait(
  thread: Pick<DoorThreadRead, 'id' | 'conversation_id'>,
  requests: DoorDataRequestRead[],
): AskWait | null {
  // A pass-along (kanban 196e9d9b) is theirs now: nobody here waits on it.
  const open = asksForThread(requests, thread)
    .filter(isWaitingAsk)
    .sort((a, b) => sentAt(a).localeCompare(sentAt(b)));
  if (open.length === 0) return null;
  const who: string[] = [];
  for (const r of open) if (!who.includes(r.target_name)) who.push(r.target_name);
  return { who, since: sentAt(open[0]) };
}

/** Everyone a live item waits on: the person its status names, else the
 *  people with an open ask on it. Empty when it waits on nobody. */
export function waitingWho(
  thread: Pick<DoorThreadRead, 'id' | 'conversation_id' | 'work_state' | 'work_state_note' | 'status'>,
  requests: DoorDataRequestRead[],
): string[] {
  const w = waitingOn(thread);
  if (w) return [w.who];
  if (thread.status !== 'routed' && thread.status !== 'open') return [];
  return openAskWait(thread, requests)?.who ?? [];
}

/** For waitingOrder: when an item's oldest open ask went out; undefined when
 *  it has none (null when it has one of unknown time). */
export function askedSinceOf(
  requests: DoorDataRequestRead[],
): (t: Pick<DoorThreadRead, 'id' | 'conversation_id'>) => string | null | undefined {
  return (t) => {
    const ask = openAskWait(t, requests);
    return ask ? ask.since : undefined;
  };
}
